/**
 * useAICreditStore.js — Zustand store for AI credit balance management.
 *
 * Provides:
 * - Real-time credit balance via Firestore onSnapshot listener
 * - BYOK fallback mode when credits are depleted
 * - AI mode detection (proxy | byok | none)
 * - Credit-aware action helpers
 *
 * Storage: Server-managed via Cloud Functions. The client only reads
 * via Firestore listener — never writes to credit docs directly.
 */

import { create } from 'zustand';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { fetchCreditBalance } from '../utils/aiProxy';
import { hasApiKey, setApiKey } from '../utils/geminiService';
import useConfigStore from './useConfigStore';

// ─── Constants (mirrored from functions/src/utils/constants.js) ───
// These literals are the OFFLINE / first-paint fallbacks. Runtime values come
// from config/public → ai.clientDefaults (see helpers below); absent config ⇒
// these exact literals, so default behavior never changes.

const RESERVE_AMOUNTS = {
  ai_coach: 8,
  voice_dump: 15,
};

const DEFAULT_MONTHLY_ALLOCATION = 50;

/** ai.clientDefaults from the live public config, or null (→ code literals). */
function configClientDefaults() {
  try {
    return useConfigStore.getState().publicConfig?.ai?.clientDefaults || null;
  } catch {
    return null; // config store unavailable (isolated tests) → literals
  }
}

/** Fallback monthly allocation used when the credit doc omits the field. */
function defaultMonthlyAllocation() {
  const v = configClientDefaults()?.defaultMonthlyAllocation;
  return typeof v === 'number' && v > 0 ? v : DEFAULT_MONTHLY_ALLOCATION;
}

/** Reserve estimate for a feature: config overlay → code literal. */
function reserveFor(feature) {
  const v = configClientDefaults()?.reserveEstimates?.[feature];
  if (typeof v === 'number' && v > 0) return v;
  return RESERVE_AMOUNTS[feature] || RESERVE_AMOUNTS.ai_coach;
}

// ─── Store ───

const useAICreditStore = create((set, get) => ({
  // ─── State ───
  credits: 0,
  monthlyAllocation: DEFAULT_MONTHLY_ALLOCATION,
  tier: 'free',
  totalUsed: 0,
  totalPurchased: 0,
  nextResetAt: null,
  byokEnabled: false,
  isLoading: true,
  error: null,

  // Internal
  _unsubscribe: null,
  _uid: null,

  // ─── Initialization (called by AuthGate after login) ───

  /**
   * Set up real-time Firestore listener on the user's credit doc.
   * Falls back to a callable function if the doc doesn't exist yet.
   */
  initCredits: (uid) => {
    const { _unsubscribe } = get();
    // Clean up previous listener
    if (_unsubscribe) _unsubscribe();

    set({ isLoading: true, _uid: uid, error: null });

    const creditRef = doc(db, 'users', uid, '_singles', 'ai_credits');

    const unsubscribe = onSnapshot(
      creditRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          set({
            credits: data.credits ?? 0,
            monthlyAllocation: data.monthlyAllocation ?? defaultMonthlyAllocation(),
            tier: data.tier ?? 'free',
            totalUsed: data.totalUsed ?? 0,
            totalPurchased: data.totalPurchased ?? 0,
            nextResetAt: data.nextResetAt ?? null,
            byokEnabled: data.byokEnabled ?? false,
            isLoading: false,
            error: null,
          });
        } else {
          // Doc doesn't exist yet — trigger seed via getCreditBalance callable
          fetchCreditBalance()
            .then((data) => {
              // The callable creates the doc, which will trigger onSnapshot again.
              // But let's also set state directly for fast boot.
              set({
                credits: data.credits,
                monthlyAllocation: data.monthlyAllocation,
                tier: data.tier,
                totalUsed: data.totalUsed,
                nextResetAt: data.nextResetAt,
                byokEnabled: data.byokEnabled,
                isLoading: false,
              });
            })
            .catch((err) => {
              console.error('[AICreditStore] Failed to seed credits:', err);
              set({ isLoading: false, error: 'Failed to load credit balance.' });
            });
        }
      },
      (err) => {
        console.error('[AICreditStore] Listener error:', err);
        set({ isLoading: false, error: 'Failed to sync credit balance.' });
      }
    );

    set({ _unsubscribe: unsubscribe });
  },

  // ─── Queries ───

  /** Check if user has enough credits for a specific feature. */
  hasCredits: (feature = 'ai_coach') => {
    return get().credits >= reserveFor(feature);
  },

  /** Check if user has any credits at all. */
  hasAnyCredits: () => get().credits > 0,

  /**
   * Determine the current AI mode:
   * - 'proxy' — Platform credits available, use server proxy
   * - 'byok' — No credits, but user has own API key
   * - 'none' — No credits AND no API key
   */
  getAIMode: (feature = 'ai_coach') => {
    const state = get();
    // If still loading, assume proxy for now
    if (state.isLoading) return 'proxy';

    if (state.credits >= reserveFor(feature)) return 'proxy';
    if (hasApiKey()) return 'byok';
    return 'none';
  },

  /** Get percentage of credits remaining (for progress bar). */
  getCreditPercentage: () => {
    const { credits, monthlyAllocation } = get();
    if (monthlyAllocation <= 0) return 0;
    return Math.round((credits / monthlyAllocation) * 100);
  },

  /** Get approximate number of remaining actions by feature. */
  getEstimatedActions: () => {
    const { credits } = get();
    return {
      coachMessages: Math.floor(credits / 3),    // ~3K tokens per message = 3 credits
      voiceDumps: Math.floor(credits / 6),        // ~6K tokens per dump = 6 credits
    };
  },

  /** Check if we're at a low-credit warning threshold. */
  getWarningLevel: () => {
    const pct = get().getCreditPercentage();
    if (pct <= 0) return 'depleted';
    if (pct <= 10) return 'critical';
    if (pct <= 20) return 'low';
    return 'ok';
  },

  // ─── BYOK Management ───

  /**
   * Enable BYOK mode — store user's API key in session.
   * The key is NOT sent to our server. It's used for direct client→Gemini calls.
   */
  enableByok: (key) => {
    if (key) {
      setApiKey(key, false); // session-only
      set({ byokEnabled: true });
    }
  },

  /** Disable BYOK — switch back to platform credits when available. */
  disableByok: () => {
    set({ byokEnabled: false });
  },

  /** Returns true if actively using BYOK (no credits + has key). */
  isUsingByok: () => {
    return get().getAIMode() === 'byok';
  },

  // ─── Cleanup ───

  cleanup: () => {
    const { _unsubscribe } = get();
    if (_unsubscribe) _unsubscribe();
    set({
      credits: 0,
      monthlyAllocation: DEFAULT_MONTHLY_ALLOCATION,
      tier: 'free',
      totalUsed: 0,
      nextResetAt: null,
      byokEnabled: false,
      isLoading: true,
      error: null,
      _unsubscribe: null,
      _uid: null,
    });
  },
}));

export default useAICreditStore;
